import { api } from './api'

const apiVersion = 'v2'

export const webStoreOrderApi = api
    .enhanceEndpoints({
        addTagTypes: ['Orders', 'Order'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getOrder: builder.query({
                query: ({ orderId, organisationId }) =>
                    `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}`,
                providesTags: [{ type: 'Order', id: 'LIST' }],
            }),

            getOrdersByCustomer: builder.query({
                query: ({ organisationId, params }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/`,
                    params: {
                        organisationId: organisationId,
                        customerId: params.customerId,
                        pageNumber: params.pageNumber,
                        pageSize: params.pageSize,
                        searchTerm: params.searchTerm || '',
                        orderedByProperty: params.orderedByProperty,
                        orderedBy: params.orderedBy,
                        status: params.status,
                        createdDateFrom: params.createdDateFrom,
                        createdDateTo: params.createdDateTo,
                    },
                }),
                providesTags: (result, _error, _arg) =>
                    result
                        ? [{ type: 'Orders', id: 'LIST' }, ...result.quotes.map(({ id }) => ({ type: 'Orders', id }))]
                        : [{ type: 'Orders', id: 'LIST' }],
            }),

            createOrder: builder.mutation({
                query: ({ organisationId, payload }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/`,
                    method: 'POST',
                    body: payload,
                }),
            }),

            updateOrder: builder.mutation({
                query: ({ order, orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}`,
                    method: 'PUT',
                    body: order,
                }),
                async onQueryStarted({ order, orderId, organisationId }, { dispatch, queryFulfilled }) {
                    dispatch(
                        webStoreOrderApi.util.updateQueryData('getOrder', { organisationId, orderId }, (draft) => {
                            Object.assign(draft, order)
                        })
                    )
                    try {
                        const res = await queryFulfilled

                        dispatch(
                            webStoreOrderApi.util.updateQueryData('getOrder', { organisationId, orderId }, (draft) => {
                                Object.assign(draft, res.data)
                            })
                        )
                    } catch {
                        //patchResult.undo()
                        dispatch(webStoreOrderApi.util.invalidateTags([{ type: 'Order', id: 'LIST' }]))
                    }
                },
            }),

            calculateOrder: builder.mutation({
                query: ({ orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/calculate`,
                    method: 'POST',
                }),
                invalidatesTags: [
                    { type: 'Order', id: 'LIST' },
                    { type: 'OrderItems', id: 'LIST' },
                    { type: 'PartLibraryEntries', id: 'LIST' },
                ],
            }),

            confirmOrder: builder.mutation({
                query: ({ orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/confirm-order`,
                    method: 'PUT',
                }),
                invalidatesTags: ['Order', 'OrderItems'],
            }),

            duplicateOrder: builder.mutation({
                query: ({ newName, orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/duplicate`,
                    method: 'POST',
                    body: {
                        newName,
                    },
                }),
                invalidatesTags: (_result, _error, _arg) => [{ type: 'Orders', id: 'LIST' }],
            }),

            getIssues: builder.query({
                query: ({ orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/issues`,
                }),
            }),

            saveOrderProgress: builder.mutation({
                query: ({ orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/save-progress`,
                    method: 'POST',
                }),
            }),
        }),
    })

export const selectOrder = (state, query) => {
    return webStoreOrderApi.endpoints.getOrder.select(query)(state)?.data ?? {}
}

export const {
    useCalculateOrderMutation,
    useConfirmOrderMutation,
    useCreateOrderMutation,
    useDuplicateOrderMutation,
    useGetIssuesQuery,
    useGetOrderQuery,
    useGetOrdersByCustomerQuery,
    useLazyGetIssuesQuery,
    useLazyGetOrderQuery,
    useLazyGetOrdersByCustomerQuery,
    useSaveOrderProgressMutation,
    useUpdateOrderMutation,
} = webStoreOrderApi
